import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface CostsAtClosingData {
  closingCosts: number;
  loanCosts: number;
  otherCosts: number;
  lenderCredits: number;
  cashToClose: number;
}

export interface CDCostsAtClosingProps {
  /** Costs at closing data */
  data: Partial<CostsAtClosingData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<CostsAtClosingData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDCostsAtClosing - Costs at Closing section on Page 1
 *
 * Summary section showing:
 * - Closing Costs total with breakdown reference
 * - Cash to Close total with breakdown reference
 */
export const CDCostsAtClosing = React.forwardRef<HTMLDivElement, CDCostsAtClosingProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update a specific cost field
    const updateCost = (field: keyof CostsAtClosingData, value: number) => {
      onChange?.({ ...data, [field]: value });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Costs at Closing" />

        <div className="border border-t-0 border-cd-black">
          <div className="grid grid-cols-2">
            {/* Closing Costs */}
            <div className="border-r border-cd-black p-3">
              <div className="flex justify-between items-start">
                <div className="cd-text-body font-bold">Closing Costs</div>
                <CDCurrency
                  value={data.closingCosts}
                  size="large"
                  editable={editable}
                  onChange={(v) => updateCost('closingCosts', v)}
                />
              </div>
              <div className="cd-text-small text-cd-gray-text mt-2">
                Includes{' '}
                <CDCurrency
                  value={data.loanCosts}
                  showDollarSign={true}
                  editable={editable}
                  onChange={(v) => updateCost('loanCosts', v)}
                  className="inline"
                />{' '}
                in Loan Costs +{' '}
                <CDCurrency
                  value={data.otherCosts}
                  showDollarSign={true}
                  editable={editable}
                  onChange={(v) => updateCost('otherCosts', v)}
                  className="inline"
                />{' '}
                in Other Costs -{' '}
                <CDCurrency
                  value={data.lenderCredits}
                  showDollarSign={true}
                  editable={editable}
                  onChange={(v) => updateCost('lenderCredits', v)}
                  className="inline"
                />{' '}
                in Lender Credits.
              </div>
              <div className="cd-text-small text-cd-gray-text mt-1">See page 2 for details.</div>
            </div>

            {/* Cash to Close */}
            <div className="p-3">
              <div className="flex justify-between items-start">
                <div className="cd-text-body font-bold">Cash to Close</div>
                <CDCurrency
                  value={data.cashToClose}
                  size="large"
                  editable={editable}
                  onChange={(v) => updateCost('cashToClose', v)}
                />
              </div>
              <div className="cd-text-small text-cd-gray-text mt-2">
                Includes Closing Costs. See Calculating Cash to Close on page 3 for details.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDCostsAtClosing.displayName = 'CDCostsAtClosing';
